import { c as _c } from "react/compiler-runtime";
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useChatMessages } from '@ai-sdk-tools/store';
import { NetworkStatus, useQuery } from '@apollo/client';
import { useIsMobile } from '@newfront-insurance/admin-ui';
import { isEmpty, isNotEmpty } from '@newfront-insurance/core';
import { CircleSpinnerInline, IconAdd2, SegmentedControlV2, useFuzzy } from '@newfront-insurance/core-ui';
import { colors } from '@newfront-insurance/core-ui';
import { cn, IconButtonAsButton, IconSearch, Input, NewfrontLogo } from '@newfront-insurance/core-ui/v2';
import { createContext, Fragment, type ReactNode, useContext, useMemo, useState } from 'react';
import { IconSidebar } from './icons';
import { OrphanedStreamsContainer } from './orphaned-streams-container';
import type { GetChatsByFilters } from '../../../queries/chats';
import { GET_CHATS_BY_FILTERS } from '../../../queries/chats';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { groupChatsByDate } from '../lib/chat-utils';
import { normalizeChatUuid } from '../lib/notification-helpers';
interface ChatSidebarContextValue {
  chatHistoryFilter: 'current-account' | 'all-accounts';
  setChatHistoryFilter: (value: 'current-account' | 'all-accounts') => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  searchResults: GetChatsByFilters;
  isLoadingChats: boolean;
}
const ChatSidebarContext = createContext<ChatSidebarContextValue | null>(null);
function useChatSidebarContext() {
  const context = useContext(ChatSidebarContext);
  if (!context) {
    throw new Error("ChatSidebar components must be used within ChatSidebar");
  }
  return context;
}
interface ChatSidebarProps {
  className?: string;
  children?: ReactNode;
}

/**
 * Chat sidebar component with history, search, and filtering.
 * Supports composable children for flexible layout customization.
 * If no children are provided, uses default composition for backward compatibility.
 *
 * @example Default usage (backward compatible):
 * ```tsx
 * <ChatSidebar />
 * ```
 *
 * @example Custom composition:
 * ```tsx
 * <ChatSidebar>
 *   <ChatSidebarNewChat />
 *   <ChatSidebarSearch />
 *   <ChatSidebarSettings>
 *     <MySettingsButton />
 *   </ChatSidebarSettings>
 *   <ChatSidebarFilterTabs />
 *   <ChatSidebarHistory />
 * </ChatSidebar>
 * ```
 *
 * Note: This component is now rendered inside ChatProvider (via NovaChatContainer),
 * so it can access store messages directly for the "New Chat" button.
 */
export function ChatSidebar(t0) {
  const $ = _c(62);
  const {
    className,
    children
  } = t0;
  const {
    isMobile
  } = useIsMobile();
  const {
    userUuid,
    accountUuid,
    setIsSidebarOpen,
    isSidebarOpen,
    isFullscreen,
    isStandaloneMode,
    appType
  } = useChatOverlayContext();
  const [chatHistoryFilter, setChatHistoryFilter] = useState(isEmpty(accountUuid) ? "all-accounts" : "current-account");
  const t1 = appType ?? undefined;
  let t2;
  if ($[0] !== t1 || $[1] !== userUuid) {
    t2 = {
      userUuid,
      appType: t1
    };
    $[0] = t1;
    $[1] = userUuid;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== userUuid) {
    t3 = isEmpty(userUuid);
    $[3] = userUuid;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = {
      variables: t2,
      skip: t3,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-and-network"
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data,
    networkStatus
  } = useQuery(GET_CHATS_BY_FILTERS, t4);
  let t5;
  if ($[8] !== data) {
    t5 = data ?? {};
    $[8] = data;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    chatsList: t6
  } = t5;
  let t7;
  if ($[10] !== t6) {
    t7 = t6 === undefined ? [] : t6;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const chats = t7;
  const isLoadingChats = networkStatus === NetworkStatus.loading && data === undefined;
  let t8;
  let t9;
  if ($[12] !== accountUuid || $[13] !== chatHistoryFilter || $[14] !== chats) {
    t9 = chatHistoryFilter === "current-account" && isNotEmpty(accountUuid) ? [...chats].filter(chat => chat.accountUuid === accountUuid) : [...chats];
    $[12] = accountUuid;
    $[13] = chatHistoryFilter;
    $[14] = chats;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  t8 = t9;
  const chatsFilteredByAccount = t8;
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      keys: ["title", "summary"]
    };
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const fuzzySearch = useFuzzy(chatsFilteredByAccount, t10);
  let t11;
  let t12;
  if ($[17] !== chatHistoryFilter || $[18] !== fuzzySearch.query || $[19] !== fuzzySearch.results || $[20] !== fuzzySearch.search || $[21] !== isLoadingChats) {
    t12 = {
      chatHistoryFilter,
      setChatHistoryFilter,
      searchQuery: fuzzySearch.query,
      setSearchQuery: fuzzySearch.search,
      searchResults: fuzzySearch.results,
      isLoadingChats
    };
    $[17] = chatHistoryFilter;
    $[18] = fuzzySearch.query;
    $[19] = fuzzySearch.results;
    $[20] = fuzzySearch.search;
    $[21] = isLoadingChats;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  t11 = t12;
  const contextValue = t11;
  if (!isSidebarOpen) {
    return null;
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <><ChatSidebarNewChat /><ChatSidebarSearch /><ChatSidebarFilterTabs /><ChatSidebarHistory /></>;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const defaultChildren = t13;
  let t14;
  if ($[24] !== isMobile || $[25] !== isSidebarOpen || $[26] !== setIsSidebarOpen) {
    t14 = isSidebarOpen && isMobile && <div className="fixed inset-0 z-[1299] bg-[#000]/70 opacity-50 backdrop-blur-xs" onClick={() => setIsSidebarOpen(false)} />;
    $[24] = isMobile;
    $[25] = isSidebarOpen;
    $[26] = setIsSidebarOpen;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = !isFullscreen;
  const t16 = isFullscreen && !isMobile;
  const t17 = isFullscreen && isMobile;
  let t18;
  if ($[28] !== t15 || $[29] !== t16 || $[30] !== t17) {
    t18 = cn("absolute -top-px h-[calc(100%+2px)]", {
      "right-full z-[-1]": t15,
      "z-overlay left-0": t16,
      "z-overlay right-0 left-0": t17
    });
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== className) {
    t19 = cn("border-border/50 relative flex h-full w-60 shrink-0 flex-col gap-2 rounded-xs rounded-r-none border-y border-l", "bg-steel-50 dark:bg-steel-500", className);
    $[32] = className;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const t20 = isMobile ? "justify-between" : "";
  let t21;
  if ($[34] !== t20) {
    t21 = cn("border-border/50 flex h-14 w-60 shrink-0 items-center justify-between border-b px-6", "text-muted-foreground gap-2", "border-border/50 flex h-14 w-60 shrink-0 items-center border-b px-6", "text-muted-foreground", t20);
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <h1 className="text-muted-foreground font-semibold tracking-widest">Nova Assist</h1>;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== isMobile || $[38] !== setIsSidebarOpen) {
    t23 = isMobile && <IconButtonAsButton onClick={() => setIsSidebarOpen(false)} className="hover:bg-steel-200/20 shrink-0"><IconSidebar /></IconButtonAsButton>;
    $[37] = isMobile;
    $[38] = setIsSidebarOpen;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== t21 || $[41] !== t23) {
    t24 = <header className={t21}>{t22}{t23}</header>;
    $[40] = t21;
    $[41] = t23;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  const t25 = children ?? defaultChildren;
  let t26;
  if ($[43] !== t25) {
    t26 = <div className="flex min-h-0 flex-1 flex-col gap-2 pt-4">{t25}</div>;
    $[43] = t25;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== isStandaloneMode) {
    t27 = isStandaloneMode && <div className={cn("pointer-events-none absolute inset-x-0 bottom-0 h-20 rounded-b-xs shadow-lg", "from-steel-50 via-steel-50/95 bg-gradient-to-t to-transparent", "dark:from-steel-500 dark:via-steel-500/95 dark:to-transparent")}><div className="flex h-full items-end justify-center pb-3"><NewfrontLogo /></div></div>;
    $[45] = isStandaloneMode;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] !== t19 || $[48] !== t24 || $[49] !== t26 || $[50] !== t27) {
    t28 = <div className={t19}>{t24}{t26}{t27}</div>;
    $[47] = t19;
    $[48] = t24;
    $[49] = t26;
    $[50] = t27;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== t18 || $[53] !== t28) {
    t29 = <div className={t18}>{t28}</div>;
    $[52] = t18;
    $[53] = t28;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  let t30;
  if ($[55] !== chats) {
    t30 = chats.length > 0 && <OrphanedStreamsContainer chats={chats} />;
    $[55] = chats;
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  let t31;
  if ($[57] !== contextValue || $[58] !== t14 || $[59] !== t29 || $[60] !== t30) {
    t31 = <ChatSidebarContext.Provider value={contextValue}>{t14}{t29}{t30}</ChatSidebarContext.Provider>;
    $[57] = contextValue;
    $[58] = t14;
    $[59] = t29;
    $[60] = t30;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  return t31;
}

/**
 * New Chat button component for the sidebar.
 * Since ChatSidebar is now rendered inside ChatProvider (via NovaChatContainer),
 * this component can access store messages directly.
 */
export function ChatSidebarNewChat() {
  const $ = _c(11);
  const {
    startNewChat,
    setIsSidebarOpen,
    setIsSettingsOpen
  } = useChatOverlayContext();
  const {
    isMobile
  } = useIsMobile();
  const messages = useChatMessages();
  const hasAnyMessages = messages.length > 0;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <IconAdd2 color={colors.brand[500]} size={16} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      background: "linear-gradient(97deg, #A4F5FF 0%, #E7FC53 122.24%)"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isMobile || $[3] !== setIsSettingsOpen || $[4] !== setIsSidebarOpen || $[5] !== startNewChat) {
    t2 = () => {
      startNewChat();
      setIsSettingsOpen(false);
      if (isMobile) {
        setIsSidebarOpen(false);
      }
    };
    $[2] = isMobile;
    $[3] = setIsSettingsOpen;
    $[4] = setIsSidebarOpen;
    $[5] = startNewChat;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = !hasAnyMessages;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="text-brand-500 pl-1">New chat</span>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t3) {
    t5 = <div className="flex shrink-0 flex-col gap-3 px-4"><MenuItem icon={t0} className="font-semibold" iconClassName="rounded-full" iconStyle={t1} onClick={t2} disabled={t3}>{t4}</MenuItem></div>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}

/**
 * Search component for the sidebar.
 * Provides fuzzy search functionality for chat history.
 */
export function ChatSidebarSearch() {
  const $ = _c(6);
  const {
    searchQuery,
    setSearchQuery
  } = useChatSidebarContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <IconSearch className="text-steel-200 size-5" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== setSearchQuery) {
    t1 = e => setSearchQuery(e.target.value);
    $[1] = setSearchQuery;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== searchQuery || $[4] !== t1) {
    t2 = <div className="flex shrink-0 flex-col gap-3 px-4"><MenuItem icon={t0} className="py-1"><Input wrapperClassName="h-7 border-0" inputClassName="bg-steel-50 px-1" placeholder="Search chats" value={searchQuery} onChange={t1} /></MenuItem></div>;
    $[3] = searchQuery;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}

/**
 * Filter tabs component for the sidebar.
 * Allows filtering chat history by account (current account vs all accounts).
 */
export function ChatSidebarFilterTabs() {
  const $ = _c(24);
  const {
    accountUuid
  } = useChatOverlayContext();
  const {
    chatHistoryFilter,
    setChatHistoryFilter
  } = useChatSidebarContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SectionTitle>Chat History</SectionTitle>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== accountUuid) {
    t1 = isEmpty(accountUuid);
    $[1] = accountUuid;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = {
      label: "Current account",
      value: "current-account",
      disabled: t1
    };
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== accountUuid) {
    t3 = isEmpty(accountUuid);
    $[5] = accountUuid;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = {
      label: "All accounts",
      value: "all-accounts",
      disabled: t3
    };
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = [t2, t4];
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== setChatHistoryFilter) {
    t6 = value => {
      setChatHistoryFilter(value as 'current-account' | 'all-accounts');
    };
    $[12] = setChatHistoryFilter;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== accountUuid || $[15] !== chatHistoryFilter) {
    t7 = isEmpty(accountUuid) ? "all-accounts" : chatHistoryFilter;
    $[14] = accountUuid;
    $[15] = chatHistoryFilter;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== accountUuid) {
    t8 = cn("!border-border/50 !bg-background mx-2 !h-8 self-center !rounded-full", {
      "opacity-50": isEmpty(accountUuid)
    });
    $[17] = accountUuid;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t5 || $[20] !== t6 || $[21] !== t7 || $[22] !== t8) {
    t9 = <div className="flex flex-col gap-2">{t0}<SegmentedControlV2 options={t5} onChange={t6} value={t7} activeColor="default" containerClassname={t8} optionClassname="!rounded-full !px-1.5 !border-transparent [&.isActive]:!bg-steel-100 !text-sm" /></div>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}

/**
 * Chat history list component for the sidebar.
 * Displays grouped chat history with search and filter support.
 */
export function ChatSidebarHistory() {
  const {
    isMobile
  } = useIsMobile();
  const {
    chatUuid,
    setIsSidebarOpen,
    setActiveChat,
    setIsSettingsOpen
  } = useChatOverlayContext();
  const {
    searchResults,
    isLoadingChats
  } = useChatSidebarContext();
  const groupedChats = useMemo(() => groupChatsByDate(searchResults), [searchResults]);
  return <div className="flex flex-1 flex-col gap-2 overflow-y-auto pb-24">
      {isLoadingChats && <MenuItem icon={<CircleSpinnerInline />} className="text-foreground">
          Loading chats...
        </MenuItem>}
      {groupedChats.map(group => <Fragment key={group.title}>
          <SectionTitle>{group.title}</SectionTitle>
          <div className="flex flex-col gap-2 px-4">
            {group.chats.map(chat => {
          const isActive = normalizeChatUuid(chat.uuid ?? '') === normalizeChatUuid(chatUuid);
          return <ChatMenuItem key={chat.uuid} onClick={() => {
            setActiveChat(chat.uuid!);
            setIsSettingsOpen(false); // Close settings when selecting a chat
            if (isMobile) {
              setIsSidebarOpen(false);
            }
          }} isActive={isActive} className="text-foreground" hasNotifications={chat.hasUnreadMessages as boolean} isInProgress={chat.activeStreamId !== null}>
                  {chat.title}
                </ChatMenuItem>;
        })}
          </div>
        </Fragment>)}
    </div>;
}

/**
 * Settings wrapper component for the sidebar.
 * Provides a slot for apps to add their own settings components.
 */
export function ChatSidebarSettings(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex shrink-0 flex-col gap-3 px-4">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function SectionTitle(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("sticky top-0 z-10 flex items-center gap-2 px-6 text-sm font-semibold tracking-widest", "bg-steel-50 text-steel-300 dark:bg-steel-500 dark:text-steel-400 uppercase");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <div className={t1}>{children}</div>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function MenuItem(t0) {
  const $ = _c(14);
  const {
    children,
    icon,
    className,
    iconClassName,
    iconStyle,
    onClick,
    disabled,
    isActive
  } = t0;
  const t1 = !onClick;
  let t2;
  if ($[0] !== className || $[1] !== isActive || $[2] !== t1) {
    t2 = cn("text-muted-foreground relative flex items-center gap-2 px-2 py-1.5 text-start", "hover:bg-steel-100 dark:hover:bg-steel-400 rounded-xs", "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent", {
      "bg-steel-400/10 hover:bg-steel-400/10 cursor-default font-medium": isActive,
      "cursor-default hover:bg-transparent": t1
    }, className);
    $[0] = className;
    $[1] = isActive;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isActive ? undefined : onClick;
  let t4;
  if ($[4] !== icon || $[5] !== iconClassName || $[6] !== iconStyle) {
    t4 = icon && <div className={cn("flex size-6 shrink-0 items-center justify-center", iconClassName)} style={iconStyle}>{icon}</div>;
    $[4] = icon;
    $[5] = iconClassName;
    $[6] = iconStyle;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== children || $[9] !== disabled || $[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <button type="button" className={t2} onClick={t3} disabled={disabled}>{t4}{children}</button>;
    $[8] = children;
    $[9] = disabled;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function ChatMenuItem(t0) {
  const $ = _c(21);
  const {
    children,
    icon,
    className,
    iconClassName,
    iconStyle,
    onClick,
    disabled,
    isActive,
    hasNotifications,
    isInProgress
  } = t0;
  const t1 = !onClick;
  let t2;
  if ($[0] !== className || $[1] !== isActive || $[2] !== t1) {
    t2 = cn("text-muted-foreground relative flex items-center justify-between gap-2 px-2 py-1.5 text-start", "hover:bg-steel-100 dark:hover:bg-steel-400 rounded-xs", "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent", {
      "bg-steel-400/10 hover:bg-steel-400/10 cursor-default font-medium": isActive,
      "cursor-default hover:bg-transparent": t1
    }, className);
    $[0] = className;
    $[1] = isActive;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isActive ? undefined : onClick;
  let t4;
  if ($[4] !== icon || $[5] !== iconClassName || $[6] !== iconStyle) {
    t4 = icon && <div className={cn("flex size-6 shrink-0 items-center justify-center", iconClassName)} style={iconStyle}>{icon}</div>;
    $[4] = icon;
    $[5] = iconClassName;
    $[6] = iconStyle;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== hasNotifications) {
    t5 = hasNotifications && <span className="bg-rebrand-400 h-2 max-h-2 min-h-2 w-2 max-w-2 min-w-2 rounded-full" />;
    $[8] = hasNotifications;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== hasNotifications || $[11] !== isInProgress) {
    t6 = isInProgress && !hasNotifications && <CircleSpinnerInline size={12} />;
    $[10] = hasNotifications;
    $[11] = isInProgress;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== children || $[14] !== disabled || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5 || $[19] !== t6) {
    t7 = <button type="button" className={t2} onClick={t3} disabled={disabled}>{t4}{children}{t5}{t6}</button>;
    $[13] = children;
    $[14] = disabled;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}